import { createContext, useState, useEffect } from "react";
import axios from "axios";
import axiosInstance from "../utils/AxiosConfig";
import TokenStorage from "../utils/TokenStorage";
import { setLogoutFunction } from "./AuthHelper";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [accessToken, setAccessToken] = useState(() => TokenStorage.getToken("accessToken"));

  useEffect(() => {
    const onTokenRefreshed = (event) => {
      if (event.detail) setAccessToken(event.detail);
    };
    window.addEventListener("tokenRefreshed", onTokenRefreshed);
    return () => window.removeEventListener("tokenRefreshed", onTokenRefreshed);
  }, []);

  // Este panel es solo para el personal de Axium: tiene su propio login en el backend (/session/core/login), que solo mira
  // las cuentas del personal. No hace falta reconocer nada por correo ni por dominio.
  const login = async (email, password) => {
    try {
      const response = await axiosInstance.post("/session/core/login", {
        email,
        password,
        user_agent: "WEB",
      });
      const { accessToken: token } = response.data.data;

      TokenStorage.setToken("accessToken", token);
      localStorage.setItem("userEmail", email.trim().toLowerCase());
      setAccessToken(token);
      return { status_code: 200 };
    } catch (error) {
      const status = error?.response?.status;
      return {
        status_code: status || 500,
        error_message: status === 401
          ? "invalid_credentials"
          : error?.response?.data?.error_message || error?.response?.data?.message || "Error desconocido",
      };
    }
  };

  // Cerrar sesión también revoca en el backend todos los tokens de la cuenta. Va con
  // axios plano (no con axiosInstance) para que un 401 no vuelva a disparar el logout.
  // Si falla, igual se cierra la sesión local: el token vence solo a los 60 minutos.
  const revokeOnServer = async (token) => {
    if (!token) return;
    try {
      await axios.post(
        `${axiosInstance.defaults.baseURL}/session/logout`,
        {},
        { headers: { Authorization: `Bearer ${token}` }, timeout: 5000 },
      );
    } catch {
      /* sin conexión o token ya inválido: no hay nada más que revocar */
    }
  };

  const logout = async () => {
    await revokeOnServer(TokenStorage.getToken("accessToken"));
    setAccessToken(null);
    TokenStorage.removeToken("accessToken");
    const language = localStorage.getItem("language");
    localStorage.clear();
    if (language) localStorage.setItem("language", language);
  };

  useEffect(() => {
    setLogoutFunction(logout);
  }, []);

  return (
    <AuthContext.Provider value={{ accessToken, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
